// ============================================================================
// Google Calendar — error classification + wire shapes + sanitiser helpers.
//
// The Calendar v3 API returns errors as:
//   {
//     "error": {
//       "code": 404,
//       "message": "Not Found",
//       "errors": [{ "domain": "...", "reason": "notFound", "message": "..." }],
//       "status": "NOT_FOUND"   // only on some endpoints
//     }
//   }
//
// We classify the most common failure modes into a discriminated error class
// so callers (dispatcher, calendar sync jobs, agents) can react without re-
// parsing Google's error shape:
//
//   - AUTH_FAILED         → token rejected / missing scopes. Instance needs
//                             re-auth (or the scope list is wrong).
//   - TOKEN_EXPIRED       → specific 401 with `reason: authError`. The dispatcher
//                             should refresh + retry once.
//   - RATE_LIMITED        → 429 / 403 rateLimitExceeded / quotaExceeded.
//   - INVALID_PAYLOAD     → 400 — programmer bug; fix the caller.
//   - CALENDAR_NOT_FOUND  → 404 on a calendarList/calendars endpoint.
//   - EVENT_NOT_FOUND     → 404 on an events.get/patch/delete endpoint.
//   - WATCH_EXPIRED       → a Watch channel is past its `expiration`; caller
//                             must re-subscribe. Google emits this as a 404
//                             on stop-channel + a silent halt of notifications.
//   - SYNC_TOKEN_EXPIRED  → 410 `fullSyncRequired`. Caller must drop the token
//                             and do a fresh full sync.
//   - PROVIDER_ERROR      → everything else (5xx, network, unknown shape).
// ============================================================================

// ============================================================================
// Wire shapes — minimum slice of the Calendar v3 schema we care about.
// ============================================================================

export interface GoogleCalendarRaw {
  id: string
  summary: string
  description?: string
  timeZone: string
  primary?: boolean
  accessRole?: string
}

export interface GoogleCalendarListResponse {
  kind?: string
  etag?: string
  nextPageToken?: string
  nextSyncToken?: string
  items?: GoogleCalendarRaw[]
}

export interface GoogleEventRaw {
  id: string
  iCalUID?: string
  summary?: string
  description?: string
  location?: string
  start?: { dateTime?: string; date?: string; timeZone?: string }
  end?: { dateTime?: string; date?: string; timeZone?: string }
  attendees?: Array<{
    email?: string
    displayName?: string
    responseStatus?: string
  }>
  status?: string
  created?: string
  updated?: string
  htmlLink?: string
}

export interface GoogleEventsListResponse {
  kind?: string
  etag?: string
  summary?: string
  description?: string
  nextPageToken?: string
  nextSyncToken?: string
  items?: GoogleEventRaw[]
}

export interface GoogleWatchResponse {
  kind?: string
  id?: string
  resourceId?: string
  resourceUri?: string
  token?: string
  expiration?: string
}

/**
 * Canonical Google API error envelope. See:
 *   https://developers.google.com/calendar/api/guides/errors
 *
 * `code` mirrors the HTTP status; `errors[].reason` is the narrowest
 * classifier (authError / rateLimitExceeded / fullSyncRequired / …).
 */
export interface GoogleErrorEnvelope {
  error: {
    code?: number
    message?: string
    status?: string
    errors?: Array<{
      domain?: string
      reason?: string
      message?: string
      location?: string
      locationType?: string
    }>
  }
}

// ============================================================================
// GoogleCalendarError — discriminated by `code` so callers can switch exhaustively.
// ============================================================================

export type GoogleCalendarErrorCode =
  | 'AUTH_FAILED'
  | 'TOKEN_EXPIRED'
  | 'RATE_LIMITED'
  | 'INVALID_PAYLOAD'
  | 'CALENDAR_NOT_FOUND'
  | 'EVENT_NOT_FOUND'
  | 'WATCH_EXPIRED'
  | 'SYNC_TOKEN_EXPIRED'
  | 'PROVIDER_ERROR'
  | 'INVALID_INPUT'

export interface GoogleCalendarErrorDetails {
  /** HTTP status of the underlying request, if known. */
  httpStatus?: number
  /** Raw provider message (sanitised). */
  providerMessage?: string
  /** Narrowest classifier from `errors[].reason`. */
  providerReason?: string
  /** Parsed Retry-After (ms) — only populated for RATE_LIMITED. */
  retryAfterMs?: number
}

export class GoogleCalendarError extends Error {
  public readonly name = 'GoogleCalendarError'
  constructor(
    public readonly code: GoogleCalendarErrorCode,
    message: string,
    public readonly details: GoogleCalendarErrorDetails = {},
  ) {
    super(message)
  }
}

/**
 * Dedicated error for webhook signature / channel-token verification — keep
 * it distinct from the API-call error class so tests can match on
 * `instanceof` without string-matching codes.
 */
export class GoogleCalendarSignatureError extends Error {
  public readonly name = 'GoogleCalendarSignatureError'
  constructor(message: string) {
    super(message)
  }
}

// ============================================================================
// Helpers — classify / parse / sanitise
// ============================================================================

/**
 * Parse a `Retry-After` header value (either delta-seconds or HTTP-date) and
 * return milliseconds. Returns `undefined` on unparseable input so callers
 * can fall back to a default backoff strategy.
 */
export function parseGoogleRetryAfterMs(
  headerValue: string | null | undefined,
): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000)
  }
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diff = parsed - Date.now()
    return diff > 0 ? diff : 0
  }
  return undefined
}

/**
 * Determine whether this is a "calendar resource" error (vs. an "event
 * resource" error). Caller must tell us because the same 404 status maps
 * to different error codes depending on the endpoint.
 */
export type GoogleResourceKind = 'calendar' | 'event' | 'watch' | 'other'

/**
 * Map a Google API error envelope + HTTP status to a `GoogleCalendarError`.
 *
 * Decision tree (evaluated top-to-bottom; first match wins):
 *   1. 410 + reason=fullSyncRequired → SYNC_TOKEN_EXPIRED
 *   2. 401 + reason=authError         → TOKEN_EXPIRED
 *   3. 401 otherwise / 403 invalidCredentials → AUTH_FAILED
 *   4. 429                            → RATE_LIMITED
 *   5. 403 + reason=rateLimitExceeded/quotaExceeded/userRateLimitExceeded → RATE_LIMITED
 *   6. 404 when `resourceKind='watch'` → WATCH_EXPIRED
 *   7. 404 when `resourceKind='calendar'` → CALENDAR_NOT_FOUND
 *   8. 404 otherwise                  → EVENT_NOT_FOUND
 *   9. 400                            → INVALID_PAYLOAD
 *  10. everything else                 → PROVIDER_ERROR
 */
export function classifyGoogleError(
  httpStatus: number,
  envelope: GoogleErrorEnvelope | null | undefined,
  resourceKind: GoogleResourceKind = 'other',
  retryAfterMs?: number,
): GoogleCalendarError {
  const rawMessage =
    envelope?.error?.message ??
    `Google Calendar API error (HTTP ${httpStatus})`
  const message = sanitizeGoogleErrorMessage(rawMessage)
  const reason = firstReason(envelope)

  const details: GoogleCalendarErrorDetails = {
    httpStatus,
    providerMessage: message,
    providerReason: reason ?? undefined,
    retryAfterMs,
  }

  // 1. Sync token expiry — highest priority because Google returns 410 which
  //    would otherwise collapse into PROVIDER_ERROR.
  if (httpStatus === 410 && reason === 'fullSyncRequired') {
    return new GoogleCalendarError('SYNC_TOKEN_EXPIRED', message, details)
  }

  // 2. Token expired — distinct from AUTH_FAILED so the dispatcher knows to
  //    try a refresh before surfacing the error to the user.
  if (httpStatus === 401 && reason === 'authError') {
    return new GoogleCalendarError('TOKEN_EXPIRED', message, details)
  }

  // 3. Generic 401 / 403 credential rejection.
  if (httpStatus === 401) {
    return new GoogleCalendarError('AUTH_FAILED', message, details)
  }
  if (httpStatus === 403 && reason === 'invalidCredentials') {
    return new GoogleCalendarError('AUTH_FAILED', message, details)
  }

  // 4-5. Rate limiting. 429 is the canonical signal; 403 with a specific
  //      reason is Google's "daily quota exhausted" / "user per-minute cap"
  //      path.
  if (httpStatus === 429) {
    return new GoogleCalendarError('RATE_LIMITED', message, details)
  }
  if (
    httpStatus === 403 &&
    (reason === 'rateLimitExceeded' ||
      reason === 'quotaExceeded' ||
      reason === 'userRateLimitExceeded')
  ) {
    return new GoogleCalendarError('RATE_LIMITED', message, details)
  }

  // 6-8. 404 resolution.
  if (httpStatus === 404) {
    if (resourceKind === 'watch') {
      return new GoogleCalendarError('WATCH_EXPIRED', message, details)
    }
    if (resourceKind === 'calendar' || reason === 'notFound') {
      // `notFound` on a /calendars/{id}/events call without further context
      // is almost always the calendar (events path 404 uses a different
      // status). Caller can override with `resourceKind='event'`.
      if (resourceKind === 'event') {
        return new GoogleCalendarError('EVENT_NOT_FOUND', message, details)
      }
      return new GoogleCalendarError('CALENDAR_NOT_FOUND', message, details)
    }
    return new GoogleCalendarError('EVENT_NOT_FOUND', message, details)
  }

  // 9. Bad request.
  if (httpStatus === 400) {
    return new GoogleCalendarError('INVALID_PAYLOAD', message, details)
  }

  return new GoogleCalendarError('PROVIDER_ERROR', message, details)
}

function firstReason(envelope: GoogleErrorEnvelope | null | undefined): string | undefined {
  const errors = envelope?.error?.errors
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0]
    return typeof first?.reason === 'string' ? first.reason : undefined
  }
  return undefined
}

/**
 * Strip Google-shaped secrets out of an error / log string before it leaves
 * the adapter. We match:
 *   - `Authorization: Bearer <token>` / bare `Bearer <token>`
 *   - `access_token=...`, `refresh_token=...` in query strings
 *   - `AIza...` — Google API keys (40 chars)
 *   - `GOCSPX-...` — Google OAuth client secrets
 *   - JWTs (`eyJ...` with three dot-separated base64url segments)
 *   - Long base64url blobs (40+ chars) — catches server tokens Google echoes
 *     back in debug messages
 *
 * Patterns are copied from `apps/api/src/services/ai-agent-processor.ts#scrubProviderError`
 * for consistency with the rest of the platform.
 *
 * Patterns intentionally redact aggressively — false positives on an error
 * string are cheap; a leaked token in Workers tail logs is catastrophic.
 */
export function sanitizeGoogleErrorMessage(raw: string): string {
  if (!raw) return raw
  let out = raw
  // `Authorization: Bearer <token>` / `Bearer <token>` — any alphanum+punct.
  out = out.replace(/(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi, '$1REDACTED')
  // Query-string style token leaks.
  out = out.replace(/(access[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(refresh[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(id[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(client[_-]?secret=)[^&\s"']+/gi, '$1REDACTED')
  // Google API keys — `AIza...` (always exactly 39 chars, but match 20+ to
  // be generous).
  out = out.replace(/\bAIza[A-Za-z0-9_-]{20,}/g, 'AIza***')
  // Google OAuth 2.0 client secrets — `GOCSPX-...`.
  out = out.replace(/\bGOCSPX-[A-Za-z0-9_-]{8,}/g, 'GOCSPX-***')
  // JWTs — anchored on `eyJ` (base64url of `{"`) so we don't false-positive
  // arbitrary dot-separated alphanumeric strings.
  out = out.replace(
    /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{8,}/g,
    'eyJ***',
  )
  // Long base64url / base64 blobs (40+ chars). Matches server access tokens
  // Google sometimes echoes in debug responses. We avoid over-scrubbing by
  // preserving pure hex strings (request ids, message digests) up to 64
  // chars — those aren't sensitive.
  out = out.replace(/[A-Za-z0-9+/_\-]{40,}={0,2}/g, (match) => {
    if (/^[0-9a-f]+$/.test(match) && match.length <= 64) return match
    return 'REDACTED'
  })
  return out
}
